import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { BookOpen, Calendar, ArrowRight, FileText, Sparkles, Heart, User, Target, Compass, Flame, Download, Printer } from 'lucide-react';
import { retreatContent } from '../data/retreatContent';

// Helper to get all days
const getAllDays = () => {
  const days = [];
  retreatContent.weeks.forEach(week => {
    week.days.forEach(day => {
      days.push({ ...day, weekNumber: week.number, pillar: week.pillar });
    });
  });
  return days;
};

const pillarIcons = {
  Relationship: Heart,
  Identity: User,
  Mission: Target,
  Purpose: Compass
};

const pillarColors = {
  Relationship: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-600', badge: 'bg-rose-500' },
  Identity: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', badge: 'bg-amber-500' },
  Mission: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-600', badge: 'bg-teal-500' },
  Purpose: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-600', badge: 'bg-violet-500' }
};

const JournalPage = ({ dailyNotes, currentDay }) => {
  const navigate = useNavigate();
  const printRef = useRef();
  const allDays = getAllDays();
  
  // Get entries with notes
  const entriesWithNotes = Object.entries(dailyNotes)
    .filter(([_, note]) => note?.trim())
    .map(([dayNum, note]) => {
      const dayInfo = allDays.find(d => d.day === parseInt(dayNum));
      return {
        day: parseInt(dayNum),
        note,
        title: dayInfo?.title || `Day ${dayNum}`,
        scripture: dayInfo?.scripture || '',
        pillar: dayInfo?.pillar || 'Relationship'
      };
    })
    .sort((a, b) => a.day - b.day); // Chronological for export

  const totalEntries = entriesWithNotes.length;
  const totalWords = entriesWithNotes.reduce((acc, e) => acc + e.note.split(/\s+/).length, 0);

  // Calculate streak
  let streak = 0;
  for (let i = 1; i <= 30; i++) {
    if (dailyNotes[i]?.trim()) streak++;
    else break;
  }

  // Export to PDF
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'DMU-Lenten-Retreat-Journal',
    pageStyle: `
      @page { 
        size: letter portrait;
        margin: 0.75in;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; }
      }
    `
  });

  // Display entries (sorted by most recent)
  const displayEntries = [...entriesWithNotes].sort((a, b) => b.day - a.day);

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Stats Header */}
      <div className="bg-gradient-to-br from-[var(--primary)] via-[var(--primary)] to-[var(--primary-dark)] rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4 mb-5">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <BookOpen className="w-8 h-8" />
            </div>
            {streak > 0 && (
              <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                <Flame className="w-4 h-4" />
              </div>
            )}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">My Journal</h1>
            <p className="text-white/80 text-sm">Your retreat reflections</p>
            {streak > 0 && (
              <p className="text-orange-300 text-xs mt-1 flex items-center gap-1">
                <Flame className="w-3 h-3" />
                {streak} day streak!
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-3xl font-bold">{totalEntries}</p>
            <p className="text-white/60 text-xs">Entries</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-3xl font-bold">{totalWords}</p>
            <p className="text-white/60 text-xs">Words</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-3xl font-bold">{Math.round((totalEntries / 30) * 100)}%</p>
            <p className="text-white/60 text-xs">Complete</p>
          </div>
        </div>
      </div>

      {/* Export Button */}
      {totalEntries > 0 && (
        <button
          onClick={handlePrint}
          className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl p-4 flex items-center justify-center gap-3 hover:shadow-lg transition-all"
          data-testid="export-journal-btn"
        >
          <Download className="w-5 h-5" />
          <span className="font-semibold">Export Journal to PDF</span>
        </button>
      )}

      {/* Write Today's Entry CTA */}
      {currentDay && currentDay >= 1 && currentDay <= 30 && !dailyNotes[currentDay]?.trim() && (
        <div 
          onClick={() => navigate(`/day/${currentDay}`)}
          className="bg-gradient-to-r from-[var(--secondary)] to-[var(--secondary)] rounded-xl p-4 text-white cursor-pointer hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Write Today's Reflection</h3>
              <p className="text-white/80 text-sm">Day {currentDay} is waiting for your thoughts</p>
            </div>
            <ArrowRight className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Journal Entries */}
      {displayEntries.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 font-bold uppercase tracking-wider px-1">
            Your Reflections
          </p>
          {displayEntries.map(entry => {
            const colors = pillarColors[entry.pillar] || pillarColors.Relationship;
            const Icon = pillarIcons[entry.pillar] || Heart;
            return (
              <div 
                key={entry.day}
                onClick={() => navigate(`/day/${entry.day}`)}
                className={`${colors.bg} rounded-xl p-4 ${colors.border} border cursor-pointer hover:shadow-md transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full ${colors.badge} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold ${colors.text}`}>
                        Day {entry.day}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">{entry.pillar}</span>
                    </div>
                    <h4 className="font-semibold text-gray-800 mb-1">{entry.title}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                      {entry.note}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0 mt-2" />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
          <div className="w-20 h-20 rounded-full bg-white shadow-inner flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="font-heading font-bold text-gray-700 mb-2 text-lg">No entries yet</h3>
          <p className="text-sm text-gray-500 mb-6 px-4">
            Start writing your reflections as you journey through the retreat. Your insights will appear here.
          </p>
          {currentDay && currentDay >= 1 && currentDay <= 30 && (
            <button
              onClick={() => navigate(`/day/${currentDay}`)}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white px-6 py-3 rounded-full font-bold hover:shadow-md transition-all"
            >
              <Calendar className="w-5 h-5" />
              Start Day {currentDay}
            </button>
          )}
        </div>
      )}

      {/* Printable Journal (hidden) */}
      <div className="hidden">
        <div ref={printRef} className="p-8 bg-white">
          <div className="text-center mb-8 border-b-2 border-[var(--primary)] pb-6">
            <h1 className="text-3xl font-bold text-[var(--primary)] mb-2">My Lenten Retreat Journal</h1>
            <p className="text-gray-600">The RIMP Way • Divine Mercy University</p>
            <p className="text-sm text-gray-500 mt-2">
              {totalEntries} entries • {totalWords} words • {Math.round((totalEntries / 30) * 100)}% complete
            </p>
          </div>
          
          {entriesWithNotes.map((entry, idx) => (
            <div key={entry.day} className="mb-6 page-break-inside-avoid">
              <div className="bg-gray-100 p-3 rounded-lg mb-2">
                <h2 className="font-bold text-[var(--primary)]">
                  Day {entry.day}: {entry.title}
                </h2>
                <p className="text-sm text-gray-600">{entry.scripture} • {entry.pillar}</p>
              </div>
              <p className="text-gray-800 leading-relaxed whitespace-pre-wrap pl-4 border-l-4 border-[var(--secondary)]">
                {entry.note}
              </p>
            </div>
          ))}
          
          <div className="mt-12 pt-6 border-t-2 border-[var(--primary)] text-center">
            <p className="text-sm text-gray-500">
              Generated from DMU Lenten Retreat App • {new Date().toLocaleDateString()}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              © Divine Mercy University • Legion of Christ & Regnum Christi
            </p>
          </div>
        </div>
      </div>

      {/* Encouragement Quote */}
      <div className="text-center pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-400 italic">
          "The unexamined life is not worth living." — Socrates
        </p>
      </div>
    </div>
  );
};

export default JournalPage;
